import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, TextField, Pagination, Stack, Button, Alert } from '@mui/material';
import { supabase } from '../../../lib/supabase';
import CaseBriefForm from './CaseBriefForm';
import { useAuth } from '../../../context/AuthContext';

const PAGE_SIZE = 10;

const CaseBriefPage = () => {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const fetchBriefs = async () => {
      setLoading(true);
      try {
        // case_brief 테이블에 접근하기 위한 기본 쿼리
        let query = supabase
          .from('case_brief')
          .select('id, title, views, kor_summary, kor_content, created_at, user_id', { count: 'exact' })
          .order('created_at', { ascending: false });
        
        // 검색어가 있으면 필터 추가
        if (search) {
          query = query.ilike('title', `%${search}%`);
        }
        
        // 페이지네이션 적용
        query = query.range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);
        
        console.log('case_brief 쿼리 실행');
        const { data, count, error } = await query;
        
        if (error) {
          console.error('case_brief 조회 오류:', error);
          throw error;
        }
        
        console.log('case_brief 조회 성공:', data?.length || 0, '개 결과');
        setBriefs(data || []);
        setTotal(count || 0);
      } catch (err) {
        console.error('게시글 로드 중 오류:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBriefs();
  }, [page, search]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleCreate = () => {
    setEditData(null);
    setFormOpen(true);
  };

  const handleEdit = (brief) => {
    setEditData(brief);
    setFormOpen(true);
  };

  const handleFormSuccess = () => {
    setFormOpen(false);
    setPage(1);
    setSearch('');
    setSearchInput('');
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Case Brief
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Case Brief를 공유하고 토론해보세요.
        </Typography>
        {!user && (
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            검색과 우클릭 활성화는 로그인 상태에서만 허용됩니다.
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mt: 2, mb: 2 }}>
          <form onSubmit={(e) => {
            e.preventDefault();
            if (user) {
              setPage(1);
              setSearch(searchInput.trim());
            }
          }} style={{ display: 'flex', gap: 8 }}>
            <TextField
              label="제목 검색"
              variant="outlined"
              size="small"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              sx={{ width: 300 }}
            />
            <Button 
              type="submit" 
              variant="contained" 
              disabled={!user}
              title={user ? "검색" : "검색하려면 로그인이 필요합니다"}
            >
              검색
            </Button>
          </form>
          {user && (
            <Button variant="contained" onClick={handleCreate} sx={{ ml: 2 }}>
              Case Brief 등록
            </Button>
          )}
        </Box>
        {loading ? (
          <CircularProgress sx={{ mt: 4 }} />
        ) : (
          <TableContainer 
            component={Paper} 
            sx={{ 
              mt: 2,
              userSelect: user ? 'auto' : 'none',
              WebkitUserSelect: user ? 'auto' : 'none',
              MozUserSelect: user ? 'auto' : 'none',
              msUserSelect: user ? 'auto' : 'none',
              position: 'relative'
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={60}>#</TableCell>
                  <TableCell>제목</TableCell>
                  <TableCell align="right">조회수</TableCell>
                  <TableCell align="center">K-개요</TableCell>
                  <TableCell align="center">K-분석</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {briefs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">게시글이 없습니다.</TableCell>
                  </TableRow>
                ) : (
                  briefs.map((brief, idx) => (
                    <TableRow
                      key={brief.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/community/casebrief/${brief.id}`)}
                    >
                      <TableCell>{total - ((page - 1) * PAGE_SIZE + idx)}</TableCell>
                      <TableCell>{brief.title}</TableCell>
                      <TableCell align="right">{brief.views || 0}</TableCell>
                      <TableCell align="center" style={{color:'#888'}}>
                        {brief.kor_summary ? '✔' : '—'}
                      </TableCell>
                      <TableCell align="center" style={{color:'#888'}}>
                        {brief.kor_content ? '✔' : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
          <Pagination
            count={Math.ceil(total / PAGE_SIZE)}
            page={page}
            onChange={handlePageChange}
            color="primary"
            siblingCount={1}
            boundaryCount={1}
            showFirstButton
            showLastButton
          />
        </Stack>
        <CaseBriefForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSuccess={handleFormSuccess}
          initialData={editData}
        />
      </Box>
    </Container>
  );
};

export default CaseBriefPage; 